import React, { memo, useCallback, useEffect, useMemo } from 'react';
import cn from 'classnames';
import useSWRInfinite from 'swr/infinite';
import { AxiosResponse } from 'axios';

import queryString from 'query-string';
import { useTranslation } from 'next-i18next';

import { MatchCardsSkeleton } from 'components/skeletons/dashboard.component';
import { OrganizationOpportunityCard } from 'components/ComponentV2/OrganizationOpportunityCard';
import { Button, Checkbox } from 'components/form';
import { SearchTypeFilters } from 'components/search/results';
import { Row } from 'components/layout';
import {
  OpportunitiesResponse,
  OpportunityAttributes,
} from 'lib/models/opportunity';
import { useSocietyGlobal } from 'lib/contexts/society-global';
import { parseAxiosPaginatedResponse } from 'lib/utils/parser';
import { API_ROUTES } from 'lib/api-routes';
import { TouchpointType } from 'lib/models/touchpoint';
import { useGlobalSearch } from 'lib/providers/GlobalSearchProvider';
import { apiInstance } from 'lib/utils/axios';

import styles from '../CareerOpportunity.module.scss';

export interface OpportunityListProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  /** Whether to fetch recommended opportunities */
  withRecommendation: boolean;
  /** Title for the component */
  title: string;
}

const PER_PAGE_COUNT = 50;

export const OpportunityList: React.FC<OpportunityListProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    withRecommendation,
    title,
  }: OpportunityListProps) => {
    const [t] = useTranslation('career-opportunities');
    const { selectedTouchpoints, setSelectedTouchpoints } = useSocietyGlobal();

    const listRef = React.useRef<HTMLDivElement | null>(null);
    const scrollToBottom = () => {
      listRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    };

    const { openSearchFilterModal, filterState } = useGlobalSearch();

    const getTouchpointListKey = (pageIndex: number) => {
      const {
        posted_by_company_ids,
        quick_apply,
        internship_type,
        ...restQuery
      } = filterState;
      const params = {
        per_page: PER_PAGE_COUNT,
        page: pageIndex + 1,
        with_details: true,
        creatable_for_ids: posted_by_company_ids,
        is_eligible_for_auto_apply: quick_apply,
        kinds: internship_type,
        ...restQuery,
        ...(withRecommendation
          ? { with_recommendation: true }
          : { without_recommendation: true }),
      };
      const qs = queryString.stringify(params, {
        arrayFormat: 'bracket',
        skipEmptyString: true,
      });

      return `${API_ROUTES.CAMPAIGNS}?${qs}`;
    };

    const {
      data: touchpointsResponse,
      error: touchpointsError,
      size,
      setSize,
    } = useSWRInfinite<AxiosResponse<OpportunitiesResponse>>(
      getTouchpointListKey,
      apiInstance.get,
      {
        revalidateOnFocus: false,
        revalidateFirstPage: false,
      }
    );

    const isAPILoading = !touchpointsResponse && !touchpointsError;
    const isLoadingMore =
      isAPILoading ||
      (size > 0 &&
        touchpointsResponse &&
        typeof touchpointsResponse[size - 1] === 'undefined');

    useEffect(() => {
      if (!isLoadingMore && size > 1) {
        scrollToBottom();
      }
    }, [isLoadingMore, size]);

    useEffect(() => {
      setSize(1);

      return () => {
        listRef.current = null;
        setSize(1);
      };
    }, []);

    const hasMoreRecords = useMemo(() => {
      const firstApplicationList = Array.isArray(touchpointsResponse)
        ? touchpointsResponse[0]
        : null;
      if (firstApplicationList) {
        return (
          (firstApplicationList?.data?.meta?.total || 0) > size * PER_PAGE_COUNT
        );
      } else {
        return false;
      }
    }, [touchpointsResponse, size]);

    const touchpoints = useMemo(() => {
      return parseAxiosPaginatedResponse<OpportunityAttributes>(
        touchpointsResponse
      );
    }, [touchpointsResponse]);

    const handleCheckboxChange = useCallback(
      (id: string, touchpointable_type: TouchpointType) => {
        setSelectedTouchpoints((prevSelected) => {
          const updatedSelected = prevSelected.some(
            (touchpoint) => touchpoint.id === id
          )
            ? prevSelected.filter((touchpoint) => touchpoint.id !== id)
            : [...prevSelected, { id, touchpointable_type }];

          return updatedSelected;
        });
      },
      [touchpoints]
    );

    const handleSelectAll = useCallback(() => {
      setSelectedTouchpoints((prevSelected) => {
        const areAllSelected = touchpoints.every(({ id }) =>
          prevSelected.some((tp) => tp.id === id)
        );

        if (areAllSelected) {
          // Unselect only these touchpoints
          return prevSelected.filter(
            (tp) => !touchpoints.some((touchpoint) => touchpoint.id === tp.id)
          );
        } else {
          // Select only the currently displayed touchpoints that are not selected yet
          const newTouchpoints = touchpoints.filter(
            ({ id }) => !prevSelected.some((tp) => tp.id === id)
          );

          return [
            ...prevSelected,
            ...newTouchpoints.map(({ id, touchpointable_type }) => ({
              id,
              touchpointable_type,
            })),
          ];
        }
      });
    }, [touchpoints]);

    const totalCount = touchpointsResponse
      ? touchpointsResponse[0]?.data?.meta?.total
      : undefined;

    const getOpportunityCard = (touchpoints: Array<OpportunityAttributes>) => {
      return (
        <div className={styles.touchpointcardList}>
          {touchpoints?.map((touchpoint) => {
            const { id, touchpointable_type } = touchpoint;
            return (
              <div key={id} className={styles['card-container']}>
                <OrganizationOpportunityCard
                  className={styles.touchpointcard}
                  key={id}
                  opportunity={touchpoint}
                  showFeaturedTag={false}
                />
                <Checkbox
                  className={styles.checkbox}
                  checked={selectedTouchpoints.some(
                    (selectedTouchpoint) => selectedTouchpoint.id === id
                  )}
                  onChange={() => handleCheckboxChange(id, touchpointable_type)}
                />
              </div>
            );
          })}
        </div>
      );
    };

    const contentClassNames = cn(
      {
        [styles.container]: true,
      },
      className
    );

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid={withRecommendation ? 'Recommended' : 'OtherOpportunity'}
        ref={listRef}
      >
        <SearchTypeFilters />

        <div className={styles['title-container']}>
          <p className={styles.title}>{t(title)}</p>
          <div className={styles['select-filter-container']}>
            <p
              className={styles['select-all']}
              onClick={() => openSearchFilterModal()}
            >
              {filterState &&
                (Object.keys(filterState).filter((key) => {
                  const value = filterState[key];
                  return (
                    (Array.isArray(value) && value.length > 0) ||
                    (typeof value === 'string' && value)
                  );
                }).length > 0
                  ? `${t('filters')} (${
                      Object.keys(filterState).filter((key) => {
                        const value = filterState[key];
                        return (
                          (Array.isArray(value) && value.length > 0) ||
                          (typeof value === 'string' && value)
                        );
                      }).length
                    })`
                  : t('filters'))}
            </p>

            <p className={styles['select-all']} onClick={handleSelectAll}>
              {t('select-all')}
            </p>
          </div>
        </div>
        {totalCount ? getOpportunityCard(touchpoints) : null}
        {totalCount === undefined || isLoadingMore ? (
          <MatchCardsSkeleton />
        ) : null}
        {totalCount !== PER_PAGE_COUNT && withRecommendation ? (
          <Row align="center" columnGap={8} className={styles['counts-row']}>
            <p>
              {t('showing-count', {
                length: touchpoints?.length,
                totalCount: totalCount,
              })}
            </p>
            {hasMoreRecords ? (
              <Button
                variant="text"
                color="blue"
                onClick={() => {
                  setSize(size + 1);
                }}
                className={styles['button-row']}
              >
                {t('show-more')}
              </Button>
            ) : null}
          </Row>
        ) : null}
      </div>
    );
  }
);

OpportunityList.displayName = 'OpportunityList';
